import { useMemo, useCallback } from 'react'
import { useDataValue, useDataChange } from 'Simple/Data.js'
import isFuture from 'date-fns/isFuture'
import {
  numberMoney,
  dateShortWithMonth,
  dateShortWithMonthAndTime,
} from 'Data/format.js'
import { getPayorName, RECEIVABLE_RTYPE } from 'Data/payments.js'

import View from './view.js'
import ActionsCell from './ActionsCell/index.js'
import StatusCell from './StatusCell/index.js'
import Expanded from './Expanded/index.js'
import NotesCellTooltip from './NotesCellTooltip/index.js'

let ENTITY_TYPE_TO_RTYPES = /** @type {const} */ ({
  INVOICE: [RECEIVABLE_RTYPE.Invoice, RECEIVABLE_RTYPE.Autopay],
  CLAIM: [
    RECEIVABLE_RTYPE.Insurance,
    RECEIVABLE_RTYPE.InitClaim,
    RECEIVABLE_RTYPE.ContClaim,
    RECEIVABLE_RTYPE.ProcClaim,
  ],
})

let tooltipValueGetter = params => ({ value: params.value })

export default function Logic(props) {
  let receivables = useDataValue({
    context: 'receivables',
    viewPath: props.viewPath,
  })
  let selectedFilter = useDataValue({
    context: 'tab',
    path: 'selected.ledger.receivables.filter_by',
    viewPath: props.viewPath,
  })
  let showFutureEntries = useDataValue({
    context: 'tab',
    path: 'selected.ledger.show_future_entries',
    viewPath: props.viewPath,
  })

  let columnDefs = useMemo(
    () => [
      {
        field: 'sys_created',
        headerName: 'Date / Time',
        initialWidth: 180,
        minWidth: 160,
        valueFormatter: params => dateShortWithMonthAndTime(params.value),
        cellRenderer: 'agGroupCellRenderer',
      },
      {
        field: 'amount',
        headerName: 'Amount',
        initialWidth: 100,
        minWidth: 80,
        valueFormatter: params => numberMoney(params.value),
        cellStyle: {
          color: '#323F4B',
          textAlign: 'right',
          fontWeight: 500,
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        wrapText: true,
        initialWidth: 140,
        cellRenderer: params => (
          <StatusCell
            {...params}
            key={params.data.id}
            viewPath={`${props.viewPath}/StatusCell(${params.data.id})`}
          />
        ),
      },
      {
        field: 'treatment',
        headerName: 'Treatment',
        initialWidth: 200,
        minWidth: 150,
        valueGetter: params => params.data.treatment?.name || '',
        cellStyle: {
          color: '#616E7C',
        },
      },
      {
        field: 'payment_account',
        headerName: 'Payor',
        initialWidth: 180,
        minWidth: 120,
        valueGetter: params => getPayorName(params.data.payment_account),
        cellStyle: {
          color: '#616E7C',
        },
      },
      {
        field: 'organization',
        headerName: 'Location',
        initialWidth: 100,
        minWidth: 100,
        valueGetter: params =>
          params.data.organization ? params.data.organization.name : '-',
        cellStyle: {
          color: '#616E7C',
        },
      },
      {
        field: 'transactions',
        headerName: 'Notes',
        initialWidth: 200,
        minWidth: 100,
        tooltipValueGetter,
        tooltipComponent: params => (
          <NotesCellTooltip
            {...params}
            viewPath={`${props.viewPath}/NotesCellTooltip(${params.data.id})`}
          />
        ),
        valueFormatter: params => {
          // showing the most recent note
          let transactions_with_notes = params.value.filter(t => !!t.notes)
          return transactions_with_notes.length > 0
            ? transactions_with_notes[transactions_with_notes.length - 1].notes
            : '-'
        },
        cellStyle: {
          color: '#616E7C',
        },
      },
      {
        field: 'balance_amount',
        headerName: 'Remaining due',
        initialWidth: 120,
        minWidth: 80,
        valueFormatter: params => numberMoney(params.value),
        cellStyle: {
          color: '#616E7C',
          textAlign: 'right',
        },
      },
      {
        field: 'due_date',
        headerName: 'Due on',
        initialWidth: 130,
        minWidth: 110,
        valueFormatter: params => dateShortWithMonth(params.value),
        cellStyle: {
          color: '#616E7C',
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        initialWidth: 80,
        minWidth: 70,
        cellRenderer: params => (
          <ActionsCell
            {...params}
            key={params.data.id}
            viewPath={`${props.viewPath}/ActionsCell(${params.data.id})`}
          />
        ),
      },
    ],
    [props.viewPath]
  )

  let filteredData = useMemo(() => {
    let data = receivables

    // Filter by entity type (Invoice/Autopay vs Claim)
    if (selectedFilter.length > 0) {
      // Build list of allowed rtypes based on selected entity types
      let allowedRtypes = selectedFilter.flatMap(
        entityType => ENTITY_TYPE_TO_RTYPES[entityType] || []
      )
      // Filter receivables to only those with allowed rtypes
      data = data.filter(item => allowedRtypes.includes(item.rtype))
    }

    // Filter out future receivables if flag is false
    if (!showFutureEntries) {
      data = data.filter(item => {
        // Keep receivables that are not in the future (today or past due)
        return !isFuture(new Date(item.due_date))
      })
    }

    return data
  }, [selectedFilter, receivables, showFutureEntries])

  let focusId = useDataValue({
    context: 'tab',
    path: 'selected.ledger.receivables.focus_id',
    viewPath: props.viewPath,
  })
  let changeFocusId = useDataChange({
    context: 'tab',
    path: 'selected.ledger.receivables.focus_id',
    viewPath: props.viewPath,
  })
  let onRowClicked = useCallback(params => {
    let node = params.node

    let target = params.event.target

    // Don't expand/collapse if clicking on a button or link (e.g. clicking on more actions)
    if (target.closest('button') || target.closest('a')) {
      return
    }

    node.setExpanded(!node.expanded)
  }, [])

  return (
    <View
      {...props}
      columnDefs={columnDefs}
      rowData={filteredData}
      height="100%"
      masterDetail={true}
      detailCellRenderer={params => (
        <Expanded
          {...params}
          key={params.data.id}
          viewPath={`${props.viewPath}/Expanded(${params.data.id})`}
        />
      )}
      detailRowAutoHeight={true}
      onRowClicked={onRowClicked}
      expandRowId={focusId}
      onExpandRowId={changeFocusId}
    />
  )
}
